import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import RemoteLiveSession from "./RemoteLiveSession";
import { toast } from "sonner";
import {
  Broadcast, Lightning, FloppyDisk, Archive, Terminal, Eye, ShieldCheck, Info,
  Monitor, LockKey, UsersThree, FileCode,
} from "@phosphor-icons/react";

const DEFAULTS = {
  allowed_users: "all", access_mode: "consent", set_vnc_password: false, vnc_password: "",
  connection: "ssh-tunnel", jump_host: "", jump_user: "tunnel", jump_port: 22, tunnel_port: 5901,
  restart_agent: true,
};

export default function RemoteDesktop() {
  const [providers, setProviders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [provider, setProvider] = useState("jamf");
  const [values, setValues] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [tabIdx, setTabIdx] = useState(0);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");
  const [mode, setMode] = useState("scripts");

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const build = async () => {
    setBuilding(true); setResult(null);
    try {
      const { data } = await api.post("/remotedesktop/build", { provider, params: values });
      setResult(data); setTabIdx(0);
      toast.success("Generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally { setBuilding(false); }
  };

  const artifacts = result?.artifacts || [];
  const active = artifacts[tabIdx];

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: `Remote Desktop (${active.label})`, provider: result.provider, output_type: "script",
          prompt: "Remote Desktop", content: active.content, language: active.language,
          filename: active.filename, deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        toast.success(`Saved ${active.label} as new project`);
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: active.content, language: active.language, filename: active.filename,
          deploy_notes: result.deploy_notes, prompt: "Remote Desktop", note: `Remote Desktop (${active.label})`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const downloadBundle = async () => {
    if (!artifacts.length) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          title: "Remote Desktop", provider: result.provider, deploy_notes: result.deploy_notes,
          files: artifacts.map((a) => ({ filename: a.filename, content: a.content })),
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${result.provider}-remote-desktop.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch { toast.error("Bundle download failed"); } finally { setBundling(false); }
  };

  const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  const Toggle = ({ k, label }) => (
    <button type="button" data-testid={`rd-${k}`} onClick={() => setVal(k, !values[k])}
      className={`flex items-center gap-2 px-3 py-2 border text-xs text-left transition-colors duration-200 ${values[k] ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
      <span className={`w-8 h-4 rounded-full relative shrink-0 transition-colors duration-200 ${values[k] ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${values[k] ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
      </span>
      {label}
    </button>
  );

  const Segmented = ({ k, options }) => (
    <div className="flex border border-[#27272A] w-fit">
      {options.map((o, i) => (
        <button key={o.value} type="button" data-testid={`rd-${k}-${o.value}`} onClick={() => setVal(k, o.value)}
          className={`flex items-center gap-1.5 px-4 py-2 text-xs ${i > 0 ? "border-l border-[#27272A]" : ""} ${values[k] === o.value ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA]"}`}>
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Broadcast size={22} weight="fill" className="text-[#00F0FF]" /> Remote Desktop
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            LiveDesktop-style secure screen sharing via built-in macOS Remote Management — any MDM, 0 tokens.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="px-8 pt-6 border-b border-[#27272A]">
        <div className="flex gap-6">
          <button data-testid="rd-mode-scripts" onClick={() => setMode("scripts")}
            className={`flex items-center gap-2 pb-3 text-sm border-b-2 -mb-px transition-colors duration-200 ${mode === "scripts" ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            <FileCode size={16} /> Deploy Scripts
          </button>
          <button data-testid="rd-mode-live" onClick={() => setMode("live")}
            className={`flex items-center gap-2 pb-3 text-sm border-b-2 -mb-px transition-colors duration-200 ${mode === "live" ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            <Broadcast size={16} /> Live Session <span className="text-[9px] border border-[#27272A] px-1.5 py-0.5 rounded">BETA</span>
          </button>
        </div>
      </div>

      {mode === "live" ? <RemoteLiveSession /> : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`rd-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls + " flex items-center gap-1.5"}><LockKey size={12} /> Authentication</label>
            <Segmented k="access_mode" options={[
              { value: "consent", label: "Ask user to accept", icon: <UsersThree size={14} className="mr-1" /> },
              { value: "credentials", label: "macOS account", icon: <LockKey size={14} className="mr-1" /> },
            ]} />
            <p className="text-[10px] text-[#71717A] font-mono mt-2">
              {values.access_mode === "consent"
                ? "The logged-in user gets a native prompt to approve the session (Apple re-prompts every ~10 min idle)."
                : "Connect with any local macOS username/password — shares their session or starts a new one."}
            </p>
          </div>

          <div>
            <label className={labelCls}>Allowed users (comma-separated, or “all”)</label>
            <input data-testid="rd-allowed-users" value={values.allowed_users} onChange={(e) => setVal("allowed_users", e.target.value)} className={inputCls} placeholder="all" />
          </div>

          <div>
            <label className={labelCls}>VNC password for 3rd-party clients (Windows / iOS)</label>
            <div className="flex flex-col md:flex-row gap-3">
              <Toggle k="set_vnc_password" label="Set a legacy VNC password" />
              <input data-testid="rd-vnc-password" type="text" disabled={!values.set_vnc_password}
                value={values.vnc_password} onChange={(e) => setVal("vnc_password", e.target.value)}
                className={inputCls + " flex-1 disabled:opacity-40"} placeholder="VNC password" />
            </div>
          </div>

          <div>
            <label className={labelCls + " flex items-center gap-1.5"}><ShieldCheck size={12} /> Secure connection</label>
            <Segmented k="connection" options={[
              { value: "ssh-tunnel", label: "Reverse SSH tunnel", icon: <ShieldCheck size={14} className="mr-1" /> },
              { value: "direct", label: "Direct (LAN / VPN)", icon: <Monitor size={14} className="mr-1" /> },
            ]} />
            {values.connection === "ssh-tunnel" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <div className="col-span-2"><label className={labelCls}>Jump host</label><input data-testid="rd-jump-host" value={values.jump_host} onChange={(e) => setVal("jump_host", e.target.value)} className={inputCls} placeholder="jump.example.com" /></div>
                <div><label className={labelCls}>SSH user</label><input data-testid="rd-jump-user" value={values.jump_user} onChange={(e) => setVal("jump_user", e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>SSH port</label><input data-testid="rd-jump-port" type="number" value={values.jump_port} onChange={(e) => setVal("jump_port", Number(e.target.value))} className={inputCls} /></div>
                <div><label className={labelCls}>Tunnel port</label><input data-testid="rd-tunnel-port" type="number" value={values.tunnel_port} onChange={(e) => setVal("tunnel_port", Number(e.target.value))} className={inputCls} /></div>
              </div>
            )}
            <p className="text-[10px] text-[#71717A] font-mono mt-2">
              {values.connection === "ssh-tunnel"
                ? "The Mac dials out to your jump host — no inbound ports are ever opened, just like LiveDesktop."
                : "Connect over the same LAN or a VPN. Simplest, but requires network reachability."}
            </p>
          </div>

          <div>
            <Toggle k="restart_agent" label="Restart the Remote Management agent after enabling" />
          </div>

          <button data-testid="rd-build-btn" onClick={build} disabled={building}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
            <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build scripts (0 tokens)"}
          </button>

          {result && active && (
            <div className="space-y-4" data-testid="rd-output">
              <div className="flex border border-[#27272A] w-fit flex-wrap">
                {artifacts.map((a, i) => (
                  <button key={a.filename} data-testid={`rd-tab-${i}`} onClick={() => setTabIdx(i)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${i > 0 ? "border-l border-[#27272A]" : ""} ${tabIdx === i ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA]"}`}>
                    <Terminal size={15} /> {a.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <select data-testid="rd-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " flex-1"}>
                  <option value="new">＋ New project</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>))}
                </select>
                <div className="flex gap-3">
                  <button data-testid="rd-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : `Save ${active.label}`}</button>
                  <button data-testid="rd-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle all"}</button>
                </div>
              </div>
              <CodeBlock code={active.content} language={active.language} filename={active.filename} />
              {result.deploy_notes && (
                <div className="border border-[#27272A] bg-[#0f0f11] p-4 text-[11px] font-mono text-[#A1A1AA] leading-relaxed" data-testid="rd-notes">
                  <div className="flex items-center gap-2 text-[#00F0FF] mb-2"><Info size={14} /> Deployment notes</div>
                  {result.deploy_notes}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview / flow */}
        <div className="bg-[#0A0A0B] p-8">
          <label className={labelCls + " flex items-center gap-1.5"}><Eye size={12} /> Connection flow</label>
          <div className="mt-3 space-y-3" data-testid="rd-preview">
            {[
              { icon: <Terminal size={16} />, t: "1 · Enable", d: "MDM runs the Enable script — turns on built-in Remote Management." },
              { icon: values.connection === "ssh-tunnel" ? <ShieldCheck size={16} /> : <Monitor size={16} />, t: values.connection === "ssh-tunnel" ? "2 · Tunnel out" : "2 · LAN / VPN", d: values.connection === "ssh-tunnel" ? "Mac dials your jump host — no inbound ports opened." : "Reachable over the same network / VPN." },
              { icon: <UsersThree size={16} />, t: values.access_mode === "consent" ? "3 · User accepts" : "3 · Authenticate", d: values.access_mode === "consent" ? "Logged-in user approves the native share prompt." : "Connect with a macOS account username/password." },
              { icon: <Monitor size={16} />, t: "4 · Control", d: "Open Screen Sharing.app / any VNC client to the address." },
            ].map((s) => (
              <div key={s.t} className="flex items-start gap-3 border border-[#27272A] p-3">
                <div className="w-8 h-8 border border-[#27272A] flex items-center justify-center text-[#00F0FF] shrink-0">{s.icon}</div>
                <div>
                  <div className="text-sm text-[#FAFAFA]">{s.t}</div>
                  <div className="text-[11px] text-[#71717A] font-mono leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 border border-[#27272A] bg-[#0f0f11] p-4">
            <div className="flex items-center gap-2 text-[#00FF41] text-xs mb-2"><ShieldCheck size={14} /> No extra app · no PPPC permissions</div>
            <p className="text-[10px] text-[#71717A] font-mono leading-relaxed">
              Uses Apple's built-in Remote Management framework — the same approach as Addigy LiveDesktop.
              Connect with Screen Sharing.app / any VNC client, or use the in-browser viewer in the "Live Session" tab.
            </p>
          </div>
        </div>
      </div>
      )}
    </Layout>
  );
}
